/*jshint esversion: 11 */
import React, { useState, useEffect } from 'react';
import {
  Row,
  Col,
  Card,
  CardBody,
  Container,
  Table,
  Label,
  FormGroup,
  Button,
  Alert,
  Input,
  Spinner
} from 'reactstrap';
import { connect } from 'react-redux';
import Breadcrumbs from '../../components/Common/Breadcrumb';
import moment from 'moment';
import { validatePermissionKey } from '../../helpers/permission';
import {
  CREATE_WEEKLY_ACTION_RECORD,
  UPDATE_WEEKLY_ACTION_RECORD,
  PUBLISH_WEEKLY_ACTION_RECORD
} from '../Utility/PermissionKeys';
import { useParams } from 'react-router-dom';
import { getBatchInfo } from '../../api/batch';
import { getWeeklyActionRecordTemplateByOfferingId } from '../../api/weeklyActionRecordTemplate';
import {
  getWeeklyActionRecord,
  createWeeklyActionRecord,
  updateWeeklyActionRecord
} from '../../api/weeklyActionRecord';
import SweetAlert from 'react-bootstrap-sweetalert';

// Separate component for viewing records
const RecordView = ({ record, onEditClick, authUser }) => {
  const daysOfWeek = [
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday'
  ];
  const dayLabels = {
    monday: 'Mon',
    tuesday: 'Tue',
    wednesday: 'Wed',
    thursday: 'Thu',
    friday: 'Fri',
    saturday: 'Sat'
  };

  return (
    <div className="mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex">
          <h5>Weekly Action Record</h5>
          {validatePermissionKey(authUser, PUBLISH_WEEKLY_ACTION_RECORD) && (
            <div className="d-flex justify-content-end mb-3 ml-2">
              <span
                className={`badge badge-pill p-2 ${
                  record.is_published
                    ? 'badge-soft-primary'
                    : 'badge-soft-danger'
                }`}
              >
                Status: {record.is_published ? 'Published' : 'Draft'}
              </span>
            </div>
          )}
        </div>
        <div>
          {validatePermissionKey(authUser, UPDATE_WEEKLY_ACTION_RECORD) && (
            <Button color="warning" className="mr-2" onClick={onEditClick}>
              <i className="ri-edit-line align-bottom me-1"></i> Edit
            </Button>
          )}
        </div>
      </div>

      <h6 className="mt-4">Daily Activities</h6>
      <div className="table-responsive">
        <Table bordered>
          <thead>
            <tr>
              <th>Activity</th>
              {daysOfWeek.map((day) => (
                <th key={day}>{dayLabels[day]}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {record.daily_activities?.map((activity, idx) => (
              <tr key={idx}>
                <td>{activity.activity}</td>
                {daysOfWeek.map((day) => (
                  <td key={`${day}-${idx}`}>
                    {activity.activity_execution[day] || '-'}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      <h6 className="mt-4">Weekly Activities</h6>
      <Table bordered>
        <thead>
          <tr>
            <th>Activity</th>
            <th>Work Done</th>
          </tr>
        </thead>
        <tbody>
          {record.weekly_activities?.map((activity, idx) => (
            <tr key={idx}>
              <td>{activity.activity}</td>
              <td>{activity.activity_execution || '-'}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

// Updated RecordForm with date selection
const RecordForm = ({
  mode,
  template,
  formData,
  onFieldChange,
  onSave,
  onCancel,
  saving,
  startDate,
  endDate,
  authUser,
  onDateChange,
  dateError
}) => {
  const daysOfWeek = [
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday'
  ];
  const dayLabels = {
    monday: 'Mon',
    tuesday: 'Tue',
    wednesday: 'Wed',
    thursday: 'Thu',
    friday: 'Fri',
    saturday: 'Sat'
  };

  const handleDailyChange = (index, day, value) => {
    onFieldChange('daily_activities', index, day, value);
  };

  const handleWeeklyChange = (index, value) => {
    onFieldChange('weekly_activities', index, null, value);
  };

  return (
    <div className="mt-4">
      <h5>
        {mode === 'create' ? 'Create New Weekly Record' : 'Edit Weekly Record'}
      </h5>
      
      {/* Date selection for both create and edit modes */}
      <Row className="mb-3">
        <Col md={6}>
          <FormGroup>
            <Label>Week Start (Monday)</Label>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => onDateChange(e.target.value)}
              disabled={saving}
            />
            {dateError && (
              <small className="text-danger">{dateError}</small>
            )}
            <small className="text-muted d-block mt-1">
              Week: {moment(startDate).format('MMM DD')} -{' '}
              {moment(endDate).format('MMM DD')}
            </small>
          </FormGroup>
        </Col>
      </Row>

      {!template ? (
        <Alert color="danger" className="mt-3">
          No template found for this offering
        </Alert>
      ) : (
        <>
          <h6 className="mt-4">Daily Activities</h6>
          <div className="table-responsive">
            <Table bordered>
              <thead>
                <tr>
                  <th>Activity</th>
                  {daysOfWeek.map((day) => (
                    <th key={day}>{dayLabels[day]}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {formData.daily_activities.map((activity, idx) => (
                  <tr key={idx}>
                    <td>{activity.activity}</td>
                    {daysOfWeek.map((day) => (
                      <td key={`${day}-${idx}`}>
                        <Input
                          type="text"
                          value={activity.activity_execution[day] || ''}
                          onChange={(e) =>
                            handleDailyChange(idx, day, e.target.value)
                          }
                          placeholder={`${dayLabels[day]} execution`}
                          disabled={saving}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          <h6 className="mt-4">Weekly Activities</h6>
          <Table bordered>
            <thead>
              <tr>
                <th>Activity</th>
                <th>Execution</th>
              </tr>
            </thead>
            <tbody>
              {formData.weekly_activities.map((activity, idx) => (
                <tr key={idx}>
                  <td>{activity.activity}</td>
                  <td>
                    <Input
                      type="text"
                      value={activity.activity_execution || ''}
                      onChange={(e) => handleWeeklyChange(idx, e.target.value)}
                      placeholder="Activity execution"
                      disabled={saving}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          <div className="d-flex justify-content-end mt-4">
            <Button
              color="danger"
              className="mr-2"
              onClick={onCancel}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              color="secondary"
              className="mr-2"
              onClick={() => onSave(false)}
              disabled={saving}
            >
              {saving ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  Saving...
                </>
              ) : (
                'Save Record'
              )}
            </Button>
            {validatePermissionKey(authUser, PUBLISH_WEEKLY_ACTION_RECORD) && (
              <Button
                color="primary"
                onClick={() => onSave(true)}
                disabled={saving}
              >
                {saving ? (
                  <>
                    <Spinner size="sm" className="mr-2" />
                    Saving...
                  </>
                ) : (
                  'Publish Record'
                )}
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );
};

const WeeklyActionRecord = ({ authUser }) => {
  const { batchId } = useParams();
  const [breadcrumbItems] = useState([
    { title: 'Weekly Action Records', link: '#' },
    { title: 'Manage Records', link: '#' }
  ]);
  const [batchLoading, setBatchLoading] = useState(true);
  const [recordLoading, setRecordLoading] = useState(false);
  const [batch, setBatch] = useState(null);
  const [offering, setOffering] = useState(null);
  const [template, setTemplate] = useState(null);
  const [record, setRecord] = useState(null);
  const [mode, setMode] = useState('view');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    daily_activities: [],
    weekly_activities: [],
    is_published: false
  });
  const [successAlert, setSuccessAlert] = useState({
    show: false,
    message: ''
  });
  const [dateError, setDateError] = useState(null);

  // View mode dates
  const [viewStartDate, setViewStartDate] = useState(() => {
    return moment().startOf('isoWeek').format('YYYY-MM-DD');
  });
  const [viewEndDate, setViewEndDate] = useState(() => {
    return moment(viewStartDate).add(6, 'days').format('YYYY-MM-DD');
  });

  // Form mode dates
  const [formStartDate, setFormStartDate] = useState(viewStartDate);
  const [formEndDate, setFormEndDate] = useState(viewEndDate);

  const getMonday = (date) => {
    return date.clone().startOf('isoWeek');
  };

  useEffect(() => {
    if (batchId) {
      handleLoadRecord();
    }
  }, [batchId, viewStartDate]);

  // Handle date changes in view mode
  const handleViewDateChange = (e) => {
    const selectedDate = moment(e.target.value);
    const dayOfWeek = selectedDate.day();

    if (dayOfWeek !== 1) {
      const monday = getMonday(selectedDate).format('YYYY-MM-DD');
      setDateError(
        'Only Mondays can be selected. Adjusted to ' +
          moment(monday).format('MMM DD')
      );
      setViewStartDate(monday);
      setViewEndDate(moment(monday).add(6, 'days').format('YYYY-MM-DD'));
    } else {
      setDateError(null);
      const newStartDate = e.target.value;
      setViewStartDate(newStartDate);
      setViewEndDate(moment(newStartDate).add(6, 'days').format('YYYY-MM-DD'));
    }
  };

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setBatchLoading(true);
        const batchData = await getBatchInfo(batchId);
        setBatch(batchData);
        setOffering(batchData.offering);
        const templateData = await getWeeklyActionRecordTemplateByOfferingId(
          batchData.offering._id
        );
        setTemplate(templateData[0]);
      } catch (error) {
        setError('Failed to load batch and template data: ' + error.message);
      } finally {
        setBatchLoading(false);
      }
    };

    if (batchId) {
      loadInitialData();
    }
  }, [batchId]);

  const initializeFormFromTemplate = () => {
    if (!template) return;

    const dailyActivities = template.information
      .filter((item) => item.type === 'daily')
      .map((item) => ({
        activity: item.activity,
        activity_execution: {
          monday: '',
          tuesday: '',
          wednesday: '',
          thursday: '',
          friday: '',
          saturday: ''
        }
      }));

    const weeklyActivities = template.information
      .filter((item) => item.type === 'weekly')
      .map((item) => ({
        activity: item.activity,
        activity_execution: ''
      }));

    setFormData({
      daily_activities: dailyActivities,
      weekly_activities: weeklyActivities,
      is_published: false
    });
  };

  // Handle date changes in form mode
  const handleFormDateChange = (dateString) => {
    const selectedDate = moment(dateString);
    const dayOfWeek = selectedDate.day();

    if (dayOfWeek !== 1) {
      const monday = getMonday(selectedDate).format('YYYY-MM-DD');
      setDateError(
        'Only Mondays can be selected. Adjusted to ' +
          moment(monday).format('MMM DD')
      );
      setFormStartDate(monday);
      setFormEndDate(moment(monday).add(6, 'days').format('YYYY-MM-DD'));
    } else {
      setDateError(null);
      setFormStartDate(dateString);
      setFormEndDate(moment(dateString).add(6, 'days').format('YYYY-MM-DD'));
    }
  };

  const handleLoadRecord = async () => {
    setRecordLoading(true);
    setError(null);
    try {
      const recordData = await getWeeklyActionRecord(
        viewStartDate,
        viewEndDate,
        batchId
      );
      setRecord(recordData);
      if (recordData) {
        setFormData({
          daily_activities: recordData.daily_activities || [],
          weekly_activities: recordData.weekly_activities || [],
          is_published: recordData.is_published || false
        });
      }
      setMode('view');
    } catch (error) {
      setError('Failed to load weekly record: ' + error.message);
    } finally {
      setRecordLoading(false);
    }
  };

  const handleCreateNew = () => {
    setError(null);
    if (!template) {
      setError('No template found for this offering');
      return;
    }
    setRecord(null);
    setMode('create');
    initializeFormFromTemplate();
    // Set form dates to current view dates
    setFormStartDate(viewStartDate);
    setFormEndDate(viewEndDate);
  };

  const handleEdit = () => {
    setMode('edit');
    // Set form dates to record's dates
    if (record) {
      setFormStartDate(record.start_date);
      setFormEndDate(record.end_date);
    }
  };

  const handleCancel = () => {
    if (record) {
      setFormData({
        daily_activities: record.daily_activities || [],
        weekly_activities: record.weekly_activities || [],
        is_published: record.is_published || false
      });
    }
    setMode('view');
  };

  const handleFieldChange = (type, index, field, value) => {
    setFormData((prevFormData) => {
      const newFormData = { ...prevFormData };

      if (type === 'daily_activities') {
        newFormData.daily_activities = [...prevFormData.daily_activities];
        newFormData.daily_activities[index] = {
          ...newFormData.daily_activities[index],
          activity_execution: {
            ...newFormData.daily_activities[index].activity_execution,
            [field]: value
          }
        };
      } else if (type === 'weekly_activities') {
        newFormData.weekly_activities = [...prevFormData.weekly_activities];
        newFormData.weekly_activities[index] = {
          ...newFormData.weekly_activities[index],
          activity_execution: value
        };
      }

      return newFormData;
    });
  };

  const handleSave = async (isPublished) => {
    setSaving(true);
    setError(null);

    try {
      const data = {
        batch_id: batchId,
        daily_activities: formData.daily_activities,
        weekly_activities: formData.weekly_activities,
        start_date: formStartDate,
        end_date: formEndDate,
        is_published: isPublished,
        offering: batch?.offering._id,
      };

      let result;
      if (mode === 'edit' && record) {
        result = await updateWeeklyActionRecord(data, record._id);
      } else {
        result = await createWeeklyActionRecord(data);
      }

      if (result?.error) {
        setError(result.error);
      } else {
        const newRecord = {
          ...data,
          _id: result?.response?.data?._id || record?._id
        };
        setRecord(newRecord);
        setMode('view');
        // Update view dates to match saved dates
        setViewStartDate(formStartDate);
        setViewEndDate(formEndDate);
      }
      setSuccessAlert({ show: true, message: 'Record updated successfully' });
    } catch (error) {
      setError('Failed to save record: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-4">
      {successAlert.show && (
        <SweetAlert
          success
          title="Updated"
          onConfirm={() => setSuccessAlert({ show: false, message: '' })}
        >
          {successAlert.message}
        </SweetAlert>
      )}
      <Container fluid>
        <Row>
          <Col xs={12}>
            <Card>
              <CardBody>
                {batchLoading ? (
                  <div className="text-center py-5">
                    <Spinner color="primary" />
                    <p className="mt-2">Loading batch and template...</p>
                  </div>
                ) : (
                  <>
                    <Row className="mb-4">
                      {error && (
                        <Col md={12}>
                          <Alert color="danger">{error}</Alert>
                        </Col>
                      )}
                      <Col md={4}>
                        <FormGroup>
                          <Label>Batch</Label>
                          <Input
                            type="text"
                            value={batch?.name || ''}
                            readOnly
                            disabled
                          />
                        </FormGroup>
                      </Col>
                      <Col md={4}>
                        <FormGroup>
                          <Label>Offering</Label>
                          <Input
                            type="text"
                            value={offering?.name || ''}
                            readOnly
                            disabled
                          />
                        </FormGroup>
                      </Col>
                      <Col md={4}>
                        <FormGroup>
                          <Label>Week Start (Monday)</Label>
                          <Input
                            type="date"
                            value={viewStartDate}
                            onChange={handleViewDateChange}
                            disabled={mode !== 'view'}
                          />
                          {dateError && mode === 'view' && (
                            <small className="text-danger mr-2">
                              {dateError}
                            </small>
                          )}
                          <small className="text-muted d-block mt-1">
                            Week: {moment(viewStartDate).format('MMM DD')} -{' '}
                            {moment(viewEndDate).format('MMM DD')}
                          </small>
                        </FormGroup>
                      </Col>
                    </Row>

                    {recordLoading && (
                      <div className="text-center py-5">
                        <Spinner color="primary" />
                        <p className="mt-2">Loading record...</p>
                      </div>
                    )}

                    {!recordLoading && mode === 'view' && record && (
                      <RecordView
                        record={record}
                        onEditClick={handleEdit}
                        authUser={authUser}
                      />
                    )}

                    {(mode === 'edit' || mode === 'create') && (
                      <RecordForm
                        mode={mode}
                        template={template}
                        formData={formData}
                        onFieldChange={handleFieldChange}
                        onSave={handleSave}
                        onCancel={handleCancel}
                        saving={saving}
                        startDate={formStartDate}
                        endDate={formEndDate}
                        authUser={authUser}
                        onDateChange={handleFormDateChange}
                        dateError={dateError}
                      />
                    )}

                    {!recordLoading && mode === 'view' && !record && (
                      <div className="text-center mt-5 py-5">
                        <h5>No record loaded</h5>
                        <p className="text-muted mb-4">
                          Start date: {moment(viewStartDate).format('MMM DD, YYYY')}{' '}
                          - End date: {moment(viewEndDate).format('MMM DD, YYYY')}
                        </p>
                        {validatePermissionKey(
                          authUser,
                          CREATE_WEEKLY_ACTION_RECORD
                        ) && (
                          <Button
                            color="success"
                            className="mt-3"
                            onClick={handleCreateNew}
                          >
                            <i className="ri-add-line align-middle mr-2"></i>
                            Create New Record
                          </Button>
                        )}
                      </div>
                    )}
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    authUser: state.Login?.user
  };
};

export default connect(mapStateToProps)(WeeklyActionRecord);